"use client";

import * as React from "react";
import { useCallback } from "react";
import { cn } from "@repo/utils/cn";
import { WistiaPlayer } from "@wistia/wistia-player-react";

export interface WistiaPlayerConfig {
  autoplay?: boolean;
  muted?: boolean;
  playerColor?: string;
  volume?: number;
  roundedPlayer?: number;
  silentAutoplay?: boolean;
  resumable?: boolean;
  playbackRate?: string;
  bigPlayButton?: boolean;
  volumeControl?: boolean;
  playBarControl?: boolean;
  qualityControl?: boolean;
  settingsControl?: boolean;
  playPauseControl?: boolean;
  fullscreenControl?: boolean;
  playbackRateControl?: boolean;
  controlsVisibleOnLoad?: boolean;
  playSuspendedOffScreen?: boolean;
  endVideoBehavior?: string;
  time?: string;
  preload?: string;
}

export interface VideoPlayerModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoId: string;
  config?: WistiaPlayerConfig;
}

export const VideoPlayerModal: React.FC<VideoPlayerModalProps> = ({
  isOpen,
  onClose,
  videoId,
  config = {},
}) => {
  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose],
  );

  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleEscape]);

  if (!isOpen) return null;

  return (
    <div
      className={cn(
        "uipm:fixed uipm:inset-0 uipm:z-[9999]",
        "uipm:flex uipm:items-center uipm:justify-center",
        "uipm:bg-white/80 uipm:backdrop-blur-sm",
        "uipm:animate-in uipm:fade-in uipm:duration-200",
      )}
      onClick={onClose}
    >
      <div
        className={cn(
          "uipm:relative uipm:w-full uipm:max-w-[90vw] uipm:max-h-[90vh]",
          "uipm:lg:max-w-[1200px]",
          "uipm:animate-in uipm:zoom-in-95 uipm:duration-200",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="uipm:relative uipm:w-full uipm:aspect-video">
          <WistiaPlayer
            className="uipm:w-full uipm:h-full"
            key={videoId}
            mediaId={videoId}
            autoplay={config.autoplay}
            muted={config.muted}
            playerColor={config.playerColor}
            volume={config.volume}
            roundedPlayer={config.roundedPlayer}
            silentAutoplay={config.silentAutoplay}
            resumable={config.resumable}
            bigPlayButton={config.bigPlayButton}
            volumeControl={config.volumeControl}
            playBarControl={config.playBarControl}
            qualityControl={config.qualityControl}
            settingsControl={config.settingsControl}
            playPauseControl={config.playPauseControl}
            fullscreenControl={config.fullscreenControl}
            playbackRateControl={config.playbackRateControl}
            controlsVisibleOnLoad={config.controlsVisibleOnLoad}
            endVideoBehavior={
              config.endVideoBehavior as
                | NonNullable<"reset" | "default" | "loop" | undefined>
                | undefined
            }
            preload={
              config.preload as
                | NonNullable<"auto" | "metadata" | "none" | undefined>
                | undefined
            }
          />
        </div>
      </div>
    </div>
  );
};
